/**
 * Actionable Follow-Up Questions parsing utilities.
 *
 * The backend appends a follow-up section to the end of an assistant answer using a
 * single section marker followed by one or more `<followup>…</followup>` tagged questions:
 *
 *   Normal answer text...
 *
 *   ◈<followup>Convert this to a FAQ</followup><followup>Add target personas</followup>
 *
 * There is no end marker — everything after the first `◈` is the follow-up payload and
 * is assumed to be the last thing in the message.
 *
 * See LDR: https://hello.atlassian.net/wiki/spaces/AA6/pages/7164582669
 */

/** Section marker (U+25C8) that denotes the start of the follow-up questions payload. */
export const ACTIONABLE_FOLLOW_UPS_MARKER = '◈';

/** Status-node localId prefix used to render the follow-ups component inline. */
export const ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX = 'actionable-follow-ups-';

/** Whether a status node localId belongs to an actionable follow-ups component. */
export const isActionableFollowUpsStatus = (localId?: string): boolean => {
	return localId != null && localId.startsWith(ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX);
};

/**
 * Matches each `<followup>…</followup>` segment in the follow-up payload. The inner capture
 * is non-greedy and allows any character (including `<`/`>`), so question text containing
 * angle brackets parses correctly. An unterminated `<followup>` with no closing tag simply
 * does not match and is dropped.
 */
const FOLLOW_UP_QUESTION_REGEX = /<followup>([\s\S]*?)<\/followup>/g;

/**
 * Parses the raw follow-up payload (the text that follows the `◈` marker) into a list of
 * question strings.
 *
 * Malformed input is dropped gracefully: unterminated brackets, empty questions, and
 * whitespace-only questions are ignored rather than rendered as broken UI.
 *
 * @param payload the text after the `◈` marker (may include the marker itself).
 */
export const parseActionableFollowUps = (payload: string): string[] => {
	if (!payload) {
		return [];
	}

	// Strip a leading marker if the caller passed the full section including `◈`.
	const markerIndex = payload.indexOf(ACTIONABLE_FOLLOW_UPS_MARKER);
	const questionsText =
		markerIndex === -1 ? payload : payload.slice(markerIndex + ACTIONABLE_FOLLOW_UPS_MARKER.length);

	const questions: string[] = [];
	for (const match of questionsText.matchAll(FOLLOW_UP_QUESTION_REGEX)) {
		const question = match[1]?.trim();
		if (question) {
			questions.push(question);
		}
	}

	return questions;
};
